import SectionWrapper from '@/components/feature/SectionWrapper';
import { Link } from 'react-router-dom';
import { programs } from '@/mocks/programs';
import SEO, { siteUrl } from '@/components/feature/SEO';

const WHATSAPP_URL = 'https://wa.me/923179699957?text=Hi%20Moin%20Khan%20Boxing%20Academy!%20I%20want%20to%20join%20training';

export default function Programs() {
  return (
    <main>
      <SEO
        title="Training Programs — Moin Khan Boxing Academy | Islamabad"
        description="Explore our boxing training programs: Private 1-on-1 coaching, Group Training, Youth Program, and Home Training. All led by international boxing champion Moin Khan in Islamabad."
        keywords="boxing programs, private boxing training, group boxing classes, youth boxing program, home boxing training, Moin Khan boxing Islamabad"
        canonicalUrl={siteUrl ? `${siteUrl}/programs` : undefined}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                item: {
                  '@type': 'Service',
                  '@id': `${siteUrl}/programs/private-training#service`,
                  name: 'Private Training 1 on 1',
                  url: `${siteUrl}/programs/private-training`,
                  description: 'One-on-one boxing training with Moin Khan, an international champion. Personalized coaching for all skill levels.',
                  provider: { '@type': 'Organization', name: 'Moin Khan Boxing Academy', '@id': `${siteUrl}/#organization` },
                  areaServed: { '@type': 'City', name: 'Islamabad' },
                },
              },
              {
                '@type': 'ListItem',
                position: 2,
                item: {
                  '@type': 'Service',
                  '@id': `${siteUrl}/programs/group-training#service`,
                  name: 'Group Training',
                  url: `${siteUrl}/programs/group-training`,
                  description: 'High-energy group boxing classes with partner drills, HIIT combos, and team motivation.',
                  provider: { '@type': 'Organization', name: 'Moin Khan Boxing Academy', '@id': `${siteUrl}/#organization` },
                  areaServed: { '@type': 'City', name: 'Islamabad' },
                },
              },
              {
                '@type': 'ListItem',
                position: 3,
                item: {
                  '@type': 'Service',
                  '@id': `${siteUrl}/programs/youth-program#service`,
                  name: 'Youth Program',
                  url: `${siteUrl}/programs/youth-program`,
                  description: 'Age-appropriate boxing training for children and teens focusing on discipline, confidence, and fitness.',
                  provider: { '@type': 'Organization', name: 'Moin Khan Boxing Academy', '@id': `${siteUrl}/#organization` },
                  areaServed: { '@type': 'City', name: 'Islamabad' },
                },
              },
              {
                '@type': 'ListItem',
                position: 4,
                item: {
                  '@type': 'Service',
                  '@id': `${siteUrl}/programs/home-training#service`,
                  name: 'Home Training',
                  url: `${siteUrl}/programs/home-training`,
                  description: 'Professional boxing coaching delivered to your doorstep with all equipment provided.',
                  provider: { '@type': 'Organization', name: 'Moin Khan Boxing Academy', '@id': `${siteUrl}/#organization` },
                  areaServed: { '@type': 'City', name: 'Islamabad' },
                },
              },
            ],
            numberOfItems: 4,
          },
        ]}
      />
      {/* Hero Banner */}
      <section className="relative pt-24 md:pt-28 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20boxing%20training%20room%20with%20heavy%20bags%20and%20punching%20equipment%2C%20dramatic%20red%20lighting%20on%20black%20background%2C%20smoke%20atmosphere%2C%20professional%20sports%20photography%2C%20high%20contrast%2C%20premium%20gym%20interior%2C%20intense%20moody%20environment&width=1920&height=600&seq=programs-hero-3&orientation=landscape"
            alt="Training programs"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-boxing-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-boxing-black via-transparent to-boxing-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <SectionWrapper>
            <span className="section-label">/ Professional Training</span>
            <h1 className="heading-mixed text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              Training <span className="text-highlight">Programs</span>
            </h1>
            <p className="font-poppins text-white/60 text-sm md:text-base max-w-xl mx-auto mt-4">
              Choose the program that fits your goals. All programs are led by an international boxer with real competition experience.
            </p>
          </SectionWrapper>
        </div>
      </section>

      {/* Programs List */}
      <section className="bg-boxing-black py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex flex-col gap-14 md:gap-20">
            {programs.map((program, index) => (
              <SectionWrapper key={program.id} delay={index * 100}>
                <div
                  className={`flex flex-col ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } gap-8 lg:gap-14 items-center`}
                >
                  {/* Image */}
                  <div className="w-full lg:w-1/2">
                    <div className="relative rounded-xl overflow-hidden border border-white/5 group">
                      <img
                        src={program.image}
                        alt={program.name}
                        className="w-full h-64 md:h-80 lg:h-96 object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-boxing-black/60 via-transparent to-transparent" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="w-full lg:w-1/2">
                    <span className="section-label mb-2">/ Program {String(index + 1).padStart(2, '0')}</span>
                    <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl mb-4">
                      {program.name}
                    </h2>
                    <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6">
                      {program.description}
                    </p>

                    <ul className="flex flex-col gap-3 mb-8">
                      {program.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 bg-boxing-red/15 rounded-full">
                            <i className="ri-check-line text-boxing-red text-sm" />
                          </span>
                          <span className="text-white/80 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={`/programs/${program.id}`}
                      className="btn-primary"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm" />
                      </span>
                      View Details
                    </Link>
                  </div>
                </div>
              </SectionWrapper>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-boxing-darker py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <SectionWrapper>
            <h2 className="heading-mixed text-3xl md:text-4xl lg:text-5xl mb-3">
              Not Sure Which <span className="text-highlight">Program?</span>
            </h2>
            <p className="text-white/60 text-sm md:text-base mb-6">
              Message us on WhatsApp and we will help you pick the right program based on your goals and schedule.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base" />
              </span>
              Enquire Now
            </a>
          </SectionWrapper>
        </div>
      </section>
    </main>
  );
}
